/* @flow */
import cx from 'classnames';
import Checkbox from 'components/Checkbox.jsx';
import List from 'components/List.jsx';
import PropTypes from 'prop-types';
import * as React from 'react';

import './styles/CheckboxList.scss';

/**
 * ## Use a `CheckboxList` to easily and consistently lay out several [Checkbox](/internal/components/Checkbox)es.
 *
 *
 * ```example
 * <CheckboxList
 *   options={
 *     new Map([
 *       ['english', 'English'],
 *       ['history', 'History'],
 *       ['math', 'Math'],
 *       ['science', 'Science'],
 *     ])
 *   }
 *   onChange={checked => window.alert('Checked!')}
 * />
 * ```
 *
 * ```example
 * <CheckboxList
 *   checkboxAlignment="right"
 *   options={
 *     new Map([
 *       ['english', 'English'],
 *       ['history', 'History'],
 *       ['math', 'Math'],
 *       ['science', 'Science'],
 *     ])
 *   }
 * />
 * ```
 *
 * ```example
 * <CheckboxList
 *   direction="horizontal"
 *   options={
 *     new Map([
 *       ['english', 'English'],
 *       ['history', 'History'],
 *       ['math', 'Math'],
 *       ['science', 'Science'],
 *     ])
 *   }
 * />
 * ```
 *
 * ```example
 * <CheckboxList
 *   options={
 *     new Map([
 *       ['english', 'English'],
 *       ['history', 'History'],
 *       ['math', 'Math'],
 *       ['science', 'Science'],
 *     ])
 *   }
 *   showRowDividers
 * />
 * ```
 */

class CheckboxList extends React.Component<Object, $FlowFixMeState> {
  static propTypes = {
    /** Checkbox input & label order */
    checkboxAlignment: PropTypes.oneOf(['default', 'left', 'right']),
    /** Checked values */
    checked: PropTypes.array,
    /** Checked values for uncontrolled inputs */
    defaultChecked: PropTypes.array,
    /** Checkbox list orientation */
    direction: PropTypes.oneOf(['vertical', 'horizontal', 'columns']),
    /** Native */
    disabled: PropTypes.array,
    /** Native */
    name: PropTypes.string,
    /**
     * Key-value pairs where the key is the value of the option. When using an array, the key and
     * value are the same.
     */
    options: PropTypes.oneOfType([PropTypes.instanceOf(Map), PropTypes.object, PropTypes.array])
      .isRequired,
    showRowDividers: PropTypes.bool,
    /** Callback when checked state changes, called with an array of checked values */
    onChange: PropTypes.func,
    eventElementName: PropTypes.string,
  };

  static defaultProps = {
    checkboxAlignment: 'default',
    showRowDividers: false,
    direction: 'vertical',
  };

  state = {
    initialChecked: this.props.defaultChecked || [],
    checked: Array.from(this.props.checked || this.props.defaultChecked || []),
  };

  componentWillReceiveProps(nextProps: Object) {
    if (this.props.checked !== nextProps.checked) {
      this.setState({ checked: Array.from(nextProps.checked) });
    }
  }

  getValue = () => {
    return this.state.checked;
  };

  handleChange = (value: string | number) => {
    const { checked } = this.state;
    const index = checked.indexOf(value);
    if (index >= 0) {
      checked.splice(index, 1);
    } else {
      checked.push(value);
    }
    this.setState({ checked });

    if (this.props.onChange) {
      this.props.onChange(checked);
    }
  };

  renderCheckbox = (value: string | number, label: string) => {
    const {
      checkboxAlignment,
      checked,
      direction,
      disabled,
      eventElementName,
      name,
      showRowDividers,
    } = this.props;
    // By default, the label is given as a child of the Checkbox
    // which renders it to the right of the checkbox input
    // If we want to float the checkbox input, we have to move the
    // label outside of the Checkbox component
    const labelAsChildOfCheckbox = checkboxAlignment === 'default';

    return (
      <li
        key={value}
        className={cx('CheckboxList-item', {
          'CheckboxList-item--left': checkboxAlignment === 'left',
          'CheckboxList-item--right': checkboxAlignment === 'right',
          'CheckboxList-item--horizontal': direction === 'horizontal',
          'CheckboxList-item--columns': direction === 'columns',
          'CheckboxList-item--divided': showRowDividers,
        })}>
        <Checkbox
          checkboxAlignment={checkboxAlignment}
          checked={checked && checked.includes(value)}
          defaultChecked={checked ? undefined : this.state.initialChecked.includes(value)}
          disabled={disabled && disabled.includes(value)}
          name={name}
          value={value}
          onChange={() => this.handleChange(value)}
          eventElementName={eventElementName}>
          {labelAsChildOfCheckbox && label}
        </Checkbox>
        {!labelAsChildOfCheckbox && label}
      </li>
    );
  };

  render() {
    const { className, onChange, options, showRowDividers, ...listProps } = this.props; // eslint-disable-line
    let items = [];

    if (options instanceof Map) {
      options.forEach((label, value) => {
        items.push(this.renderCheckbox(value, label));
      });
    } else if (Array.isArray(options)) {
      items = options.map((label, index) => {
        return this.renderCheckbox(index, label);
      });
    } else {
      items = Object.keys(options).map(value => {
        return this.renderCheckbox(value, options[value]);
      });
    }

    return (
      <List {...listProps} className={cx('CheckboxList', className)}>
        {items}
      </List>
    );
  }
}

export default CheckboxList;
